/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */


import React from 'react';
import './SubscriptionBanner.scss';
import { Button } from 'reactstrap';
import { RootInitiativeData, Subscription } from '../../types/initiative';
import G17Client from '../../services/G17Client';
import { currentTimestamp } from '../../utils/date';
import { addSiteError } from '../../slice/siteAlertsSlice';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppConfig } from '../../types/app';
import RequestDemoModal from '../request-demo-modal';
import { useToggle } from '../../hooks/useToggle';
import { SubscriptionService } from '../../services/SubscriptionService';

interface Props {
  appConfig: AppConfig | undefined;
  organization?: RootInitiativeData;
  subscription?: Subscription;
}

const getTrialMessage = (daysLeft: number, trialLength: number) => {

  if (daysLeft < 1) {
    return <span>{daysLeft} days left of {trialLength} day free trial.</span>;
  }

  return <span>{daysLeft} days left of {trialLength} day free trial.</span>;
};

const shouldDisplay = (subscription: Subscription, organization: RootInitiativeData) => {
  if (subscription.status !== 'trialing') {
    return false
  }
  // Subscription with trial and without default payment method
  return !organization.customer?.defaultPaymentMethod;
};

export const SubscriptionBanner = ({ organization, appConfig, subscription }: Props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isOpen, toggle] = useToggle(false);

  if (!organization || !subscription || !shouldDisplay(subscription, organization)) {
    return null;
  }

  const { trialEnd, trialStart } = subscription;

  const days = 3600 * 24;
  const trialLength = trialEnd && trialStart ? Math.round((trialEnd - trialStart) / days) : 0;
  const daysLeft = trialEnd ? Math.round((trialEnd - currentTimestamp()) / days) : 0;
  const canUpgrade = SubscriptionService.hasUpgradeableSub(appConfig, organization.calculatedSubscriptions);

  const manageSubscription = () => {
    G17Client.addPaymentDetails({
      initiativeId: organization._id,
      returnUrl: location.pathname
    })
      .then(s => {
        window.location.href = s.url;
      })
      .catch((e: Error) => {
        dispatch(addSiteError(e))
      })
  };

  return (
    <div className='banner-container'>

      <div className='wrapper'>
        <div className='banner-info'>
          <i className='fa fa-2x fas fa-info-circle mr-2' />
          {getTrialMessage(daysLeft, trialLength)}
        </div>

        <div className='banner-content'>
          {
            canUpgrade ? (
              <Button size={'sm'} color='link' onClick={manageSubscription}>
                <u>Add your credit card details to subscribe.</u>
              </Button>
            ) : (
              <Button size={'sm'} color='link' onClick={toggle}>
                <u>Contact us to find out more and subscribe.</u>
              </Button>
            )
          }
        </div>
        <RequestDemoModal productCode={appConfig?.productCode} isOpen={isOpen} title={'Contact us'} toggle={toggle} />
      </div>
    </div>
  );
};
